import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import SingleCare from '../assets/SingleCare-logo.png';

import ttsAudio1 from '../assets/sound/tts_09_1.mp3'; // 매우낮음
import ttsAudio2 from '../assets/sound/tts_09_2.mp3'; // 낮음
import ttsAudio3 from '../assets/sound/tts_09_3.mp3'; // 적정
import ttsAudio4 from '../assets/sound/tts_09_4.mp3'; // 높음
import ttsAudio5 from '../assets/sound/tts_09_5.mp3'; // 매우높음

import ttsAudio_a from '../assets/sound/tts_10.mp3';
import ttsAudio_b from '../assets/sound/tts_11.mp3';
import ttsAudio_c from '../assets/sound/tts_12.mp3';

import ttsAudio_en from '../assets/sound/en/9.mp3';
import ttsAudio_jp from '../assets/sound/jp/9.mp3';
import ttsAudio_cn from '../assets/sound/cn/9.mp3';

// ====== 신체나이 계산 유틸 ======
const AGE_REF_V1 = [
    { band: '20-29', min: 20, max: 29, hrAvg: 70, hrSd: 8, rmssdAvg: 45, rmssdSd: 15, rrAvg: 16 },
    { band: '30-39', min: 30, max: 39, hrAvg: 71, hrSd: 8, rmssdAvg: 38, rmssdSd: 13, rrAvg: 16 },
    { band: '40-49', min: 40, max: 49, hrAvg: 72, hrSd: 8, rmssdAvg: 30, rmssdSd: 11, rrAvg: 16 },
    { band: '50-59', min: 50, max: 59, hrAvg: 73, hrSd: 9, rmssdAvg: 24, rmssdSd: 9, rrAvg: 16 },
    { band: '60-69', min: 60, max: 69, hrAvg: 74, hrSd: 9, rmssdAvg: 20, rmssdSd: 8, rrAvg: 16 },
    { band: '70+', min: 70, max: 110, hrAvg: 75, hrSd: 10, rmssdAvg: 17, rmssdSd: 7, rrAvg: 16 },
];

const BODYAGE_WEIGHTS = { rmssd: 0.4, hr: 0.2, rr: 0.15, stress: 0.25 };

function birthToYmd(birth) {
    const s = String(birth ?? '').replace(/\D/g, '');
    if (s.length === 8) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(birth))) return String(birth);
    return null;
}

function calcAgeYears(dobYmd, now = new Date()) {
    if (!dobYmd) return null;
    const [y, m, d] = dobYmd.split('-').map(Number);
    const dob = new Date(y, (m || 1) - 1, d || 1);
    let age = now.getFullYear() - dob.getFullYear();
    const hadBirthday = now.getMonth() > dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() >= dob.getDate());
    if (!hadBirthday) age -= 1;
    return age;
}

function getAgeRef(ageYears) {
    const a = Math.max(20, Number(ageYears ?? 20));
    return AGE_REF_V1.find(r => a >= r.min && a <= r.max) || AGE_REF_V1[0];
}

function zRmssd(rmssd, avg, sd) {
    return (rmssd - avg) / Math.max(sd, 1e-6);
}

function zHr(hr, avg, sd) {
    return (avg - hr) / Math.max(sd, 1e-6);
}

function zRr(rr, rrAvg = 16) {
    return -Math.abs(rr - rrAvg) / 4;
}

function zBaevsky(baevskySi) {
    const x = Math.max(1, Number(baevskySi));
    const lx = Math.log(x);
    const avg = Math.log(350);
    const sd = 0.35;
    return (avg - lx) / Math.max(sd, 1e-6);
}

function computeBodyAgeFromData(data, opts = {}) {
    const { includeStress = true, kYearsPerBIS = 4, clampDeltaYears = 15, weights = BODYAGE_WEIGHTS, now = new Date() } = opts;

    const dobYmd = birthToYmd(data?.birth);
    const chrono = calcAgeYears(dobYmd, now);

    const hr = Math.round(Number(data?.hr));
    const rr = Math.round(Number(data?.rr));
    const rmssd = Number(data?.rmssd);
    const baevsky = Number(data?.baevsky_si);

    if (chrono == null || !Number.isFinite(hr) || !Number.isFinite(rr) || !Number.isFinite(rmssd)) {
        return { ok: false, reason: 'invalid_input', bodyAgeYears: null, chronologicalAgeYears: chrono };
    }

    const ref = getAgeRef(chrono);

    const z1 = zRmssd(rmssd, ref.rmssdAvg, ref.rmssdSd);
    const z2 = zHr(hr, ref.hrAvg, ref.hrSd);
    const z3 = zRr(rr, ref.rrAvg);

    let bis = weights.rmssd * z1 + weights.hr * z2 + weights.rr * z3;

    let z4 = null;
    if (includeStress && Number.isFinite(baevsky)) {
        z4 = zBaevsky(baevsky);

        const wSum = weights.rmssd + weights.hr + weights.rr + weights.stress;
        const norm = wSum > 0 ? 1 / wSum : 1;
        bis = norm * (weights.rmssd * z1 + weights.hr * z2 + weights.rr * z3 + weights.stress * z4);
    }

    let bodyAge = chrono - bis * kYearsPerBIS;
    bodyAge = Math.min(Math.max(bodyAge, chrono - clampDeltaYears), chrono + clampDeltaYears);
    bodyAge = Math.max(0, bodyAge);

    const round1 = v => Math.round(v * 10) / 10;

    return {
        ok: true,
        bodyAgeYears: round1(bodyAge),
        chronologicalAgeYears: chrono,
        band: ref.band,
        bis: round1(bis),
        dobYmd,
        components: {
            rmssd: { z: round1(z1), w: weights.rmssd },
            hr: { z: round1(z2), w: weights.hr },
            rr: { z: round1(z3), w: weights.rr },
            baevsky: includeStress ? { z: z4 == null ? null : round1(z4), w: weights.stress } : null,
        },
    };
}

function computeLifeAgeFromBaseLE({ chronologicalAgeYears, bioAgeYears, baseLE = 83.7, minAboveChrono = 0.1, maxLE = 110 }) {
    if (!Number.isFinite(chronologicalAgeYears) || !Number.isFinite(bioAgeYears)) return null;

    let lifeAge = baseLE + (chronologicalAgeYears - bioAgeYears);
    lifeAge = Math.max(lifeAge, chronologicalAgeYears + minAboveChrono);
    lifeAge = Math.min(lifeAge, maxLE);

    return Math.round(lifeAge * 10) / 10;
}

const Result = () => {
    const { t, i18n } = useTranslation();
    const componentRef1 = useRef(null);
    const audioRef = useRef(null);

    const [audioQueue, setAudioQueue] = useState([]);
    const [highlightIndex, setHighlightIndex] = useState(null);

    const location = useLocation();
    const userInfo = { ...(location.state || {}) };
    const navigate = useNavigate();
    const returnPath = location.state?.returnPath || '/monitoring/realtimelist';

    const ttsAudioList = [null, ttsAudio1, ttsAudio2, ttsAudio3, ttsAudio4, ttsAudio5];
    const followUpAudios = [ttsAudio_a, ttsAudio_b, ttsAudio_c];

    const [bucketTime, setbucketTime] = useState('');
    const [HrTime, sethrTime] = useState(0);
    const [RrTime, setrrTime] = useState(0);

    const [RMSSD_set, setrmssd] = useState(0.0);
    const [SDNN_set, setsdnn] = useState(0.0);
    const [pNN50_set, setpnn50] = useState(0.0);
    const [baevsky_si_set, setbaevsky] = useState(0.0);

    const [Stress_LV, setstresslevel] = useState(0.0);
    const [Stress_LV_TEXT, setstresslevel_text] = useState('');

    const [device_sn, SetDevice_sn] = useState(0);
    const [device_no, SetDevice_no] = useState(0);
    const [member_no, SetMemeber_no] = useState(0);
    const [name, SetName] = useState('');
    const [sex, SetSex] = useState(0);
    const [birth, SetBirth] = useState(0);

    const [bodyAge, setBodyAge] = useState(null);
    const [chronoAge, setChronoAge] = useState(null);
    const [bodyAgeBand, setBodyAgeBand] = useState('');
    const [bodyAgeBIS, setBodyAgeBIS] = useState(null);
    const [lifeAge, setLifeAge] = useState(null);

    const [RMSSD_level, setrmssd_level] = useState(0);
    const [SDNN_level, setsdnn_level] = useState(0);
    const [pNN50_level, setpnn50_level] = useState(0);

    // =========================
    // ✅ MN TTS (Blob) + 캐싱
    // =========================
    const MN_TTS_ENDPOINT = 'http://127.0.0.1:18791/api/tts/mn';
    const mnTtsUrlRef = useRef(null);
    const mnTtsCacheRef = useRef(new Map());
    const mnTtsInFlightRef = useRef(false);

    const buildMnTtsText = () => {
        const _chrono = chronoAge ?? '';
        const _body = bodyAge ?? '';
        const _life = lifeAge ?? '';
        const _hr = Number.isFinite(HrTime) ? HrTime : '';
        const _rr = Number.isFinite(RrTime) ? RrTime : '';
        const _stressText = Stress_LV_TEXT ?? '';

        return `
Биет дохионы хэмжилтийн үр дүнг танилцуулж байна.
Таны одоогийн нас ${_chrono} настай бөгөөд биеийн нас ${_body} байна.
Хүлээгдэж буй наслалт ${_life} байна.
Дундаж зүрхний цохилт ${_hr} BPM бөгөөд дундаж амьсгалын тоо ${_rr} BPM байна.
Стрессийн түвшин ${_stressText} байна.
`.trim();
    };

    const playMnTtsBlob = async () => {
        try {
            if (chronoAge == null || bodyAge == null || lifeAge == null) return;

            const text = buildMnTtsText();
            if (!text) return;
            if (mnTtsInFlightRef.current) return;

            const voice = 'mn-MN-BataaNeural';
            const rate = '+0%';
            const pitch = '+0Hz';
            const volume = '+0%';
            const cacheKey = `${voice}|${rate}|${pitch}|${volume}|${text}`;

            const cachedUrl = mnTtsCacheRef.current.get(cacheKey);
            if (cachedUrl) {
                const audio = audioRef.current;
                if (!audio) return;

                if (mnTtsUrlRef.current && mnTtsUrlRef.current !== cachedUrl) {
                    URL.revokeObjectURL(mnTtsUrlRef.current);
                }
                mnTtsUrlRef.current = cachedUrl;

                audio.pause();
                audio.currentTime = 0;
                audio.src = cachedUrl;
                await audio.play().catch(e => console.warn('MN TTS autoplay failed:', e));
                return;
            }

            mnTtsInFlightRef.current = true;

            const res = await axios.post(MN_TTS_ENDPOINT, { text, voice, rate, pitch, volume }, { responseType: 'blob', timeout: 20000 });

            const blob = res.data;
            const url = URL.createObjectURL(blob);

            mnTtsCacheRef.current.set(cacheKey, url);

            if (mnTtsUrlRef.current && mnTtsUrlRef.current !== url) {
                URL.revokeObjectURL(mnTtsUrlRef.current);
            }
            mnTtsUrlRef.current = url;

            const audio = audioRef.current;
            if (!audio) return;

            audio.pause();
            audio.currentTime = 0;
            audio.src = url;
            await audio.play().catch(e => console.warn('MN TTS autoplay failed:', e));
        } catch (e) {
            console.warn('MN TTS error:', e?.response?.status, e?.message);
        } finally {
            mnTtsInFlightRef.current = false;
        }
    };

    useEffect(() => {
        if (i18n.language !== 'mn') return;
        if (chronoAge == null || bodyAge == null || lifeAge == null) return;
        playMnTtsBlob();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language, chronoAge, bodyAge, lifeAge, HrTime, RrTime, Stress_LV_TEXT]);

    useEffect(() => {
        return () => {
            try {
                if (mnTtsUrlRef.current) URL.revokeObjectURL(mnTtsUrlRef.current);
                for (const url of mnTtsCacheRef.current.values()) URL.revokeObjectURL(url);
                mnTtsCacheRef.current.clear();
            } catch {}
        };
    }, []);

    useEffect(() => {
        if (Stress_LV >= 1 && Stress_LV <= 5 && i18n.language === 'ko') {
            const initial = ttsAudioList[Stress_LV];
            setAudioQueue([initial, ...followUpAudios]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Stress_LV]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || audioQueue.length === 0) return;

        let timeoutId = null;

        const playNext = queue => {
            if (queue.length === 0) {
                setHighlightIndex(null);
                return;
            }

            const [currentAudio] = queue;

            const highlightMap = {
                [ttsAudio_a]: 0,
                [ttsAudio_b]: 1,
                [ttsAudio_c]: 2,
            };
            setHighlightIndex(highlightMap[currentAudio] ?? null);

            audio.src = currentAudio;
            audio.play().catch(err => console.warn('Autoplay failed:', err));
        };

        const handleEnded = () => {
            setAudioQueue(prev => {
                const rest = prev.slice(1);

                if (rest.length > 0) {
                    timeoutId = setTimeout(() => {
                        playNext(rest);
                    }, 1500);
                } else {
                    setHighlightIndex(null);
                }

                return rest;
            });
        };

        audio.addEventListener('ended', handleEnded);
        playNext(audioQueue);

        return () => {
            clearTimeout(timeoutId);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [audioQueue]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = () => {
        try {
            const data = userInfo;

            sethrTime(Math.round(Number(data.hr)));
            setrrTime(Math.round(Number(data.rr)));
            setbucketTime(data.date);

            SetDevice_sn(data.device_sn);
            SetDevice_no(data.device_no);
            SetMemeber_no(data.member_no);
            SetName(data.name);
            SetSex(data.sex);
            SetBirth(data.birth);

            setrmssd(parseFloat(Number(data.rmssd).toFixed(1)));
            setsdnn(parseFloat(Number(data.sdnn).toFixed(1)));
            setpnn50(parseFloat(Number(data.pnn50).toFixed(1)));
            setbaevsky(data.baevsky_si);

            if (Math.round(Number(data.rmssd)) <= 20) setrmssd_level(1);
            else if (Math.round(Number(data.rmssd)) > 20 && Math.round(Number(data.rmssd)) < 80) setrmssd_level(2);
            else setrmssd_level(3);

            if (Math.round(Number(data.sdnn)) <= 30) setsdnn_level(1);
            else if (Math.round(Number(data.sdnn)) > 30 && Math.round(Number(data.sdnn)) < 100) setsdnn_level(2);
            else setsdnn_level(3);

            if (Math.round(Number(data.pnn50)) <= 20) setpnn50_level(1);
            else setpnn50_level(2);

            if (Number(data.baevsky_si) <= 100) {
                setstresslevel(1);
                setstresslevel_text(t('매우낮음'));
            } else if (Number(data.baevsky_si) <= 300) {
                setstresslevel(2);
                setstresslevel_text(t('낮음'));
            } else if (Number(data.baevsky_si) <= 500) {
                setstresslevel(3);
                setstresslevel_text(t('적정'));
            } else if (Number(data.baevsky_si) <= 700) {
                setstresslevel(4);
                setstresslevel_text(t('높음'));
            } else {
                setstresslevel(5);
                setstresslevel_text(t('매우높음'));
            }

            const rBodyAge = computeBodyAgeFromData(data, {
                includeStress: true,
                kYearsPerBIS: 4,
                clampDeltaYears: 15,
            });

            if (rBodyAge.ok) {
                setBodyAge(rBodyAge.bodyAgeYears);
                setChronoAge(rBodyAge.chronologicalAgeYears);
                setBodyAgeBand(rBodyAge.band);
                setBodyAgeBIS(rBodyAge.bis);

                const la = computeLifeAgeFromBaseLE({
                    chronologicalAgeYears: rBodyAge.chronologicalAgeYears,
                    bioAgeYears: rBodyAge.bodyAgeYears,
                    baseLE: 83.7,
                    minAboveChrono: 0.1,
                    maxLE: 110,
                });
                setLifeAge(la);
            } else {
                setBodyAge(null);
                setChronoAge(rBodyAge.chronologicalAgeYears ?? null);
                setBodyAgeBand('');
                setBodyAgeBIS(null);
                setLifeAge(null);
            }
        } catch (error) {
            console.log('Error fetching data:', error);
        }
    };

    const handlePrint = useReactToPrint({
        contentRef: componentRef1,
        documentTitle: '생체신호측정리포트',
        pageStyle: '@page { size: A4; ratio:57%;margin:4; }body {-webkit-print-color-adjust: exact;}',
    });

    const goBackToMonitoringDetail = () => {
        navigate(returnPath);
    };

    const sdnn_per = SDNN_set <= 10 ? SDNN_set : SDNN_set - 7;
    const pnn_per = pNN50_set === 100 ? pNN50_set - 8 : pNN50_set;
    const lng = i18n.language;

    return (
        <div className="stress_content">
            <main className="main-container">
                {lng === 'ko' && <audio ref={audioRef} src={ttsAudioList[Stress_LV]} autoPlay />}
                {lng === 'en' && <audio ref={audioRef} src={ttsAudio_en} autoPlay />}
                {lng === 'jp' && <audio ref={audioRef} src={ttsAudio_jp} autoPlay />}
                {lng === 'cn' && <audio ref={audioRef} src={ttsAudio_cn} autoPlay />}
                {lng === 'mn' && <audio ref={audioRef} />}

                <div className="report_area" ref={componentRef1}>
                    <div className="top_area">
                        <img src={SingleCare} className="top_logo_report" />
                        <h2>{t('Single Care 생체신호 측정 리포트')}</h2>
                    </div>

                    <div className="desc_area">
                        <table className="report_table">
                            <colgroup>
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '15%' }} />
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '45%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>{t('성명')}</th>
                                    <th>{t('성별')}</th>
                                    <th>{t('생년월일')}</th>
                                    <th>{t('측정일')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{name}</td>
                                    <td>{{ 1: t('남성'), 2: t('여성') }[sex] || '-'}</td>
                                    <td>{birth}</td>
                                    <td className={!bucketTime ? 'muted' : ''}>{bucketTime || '-'}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="life_age_card life_age_card_big">
                            <div className="life_age_title">
                                {t('예상 수명')} <span className="sub">({t('기대수명')})</span>
                            </div>

                            <div className="life_age_value future">
                                {lifeAge != null ? (
                                    <>
                                        <span className="life_age_num">{lifeAge}</span>
                                        <span className="life_age_unit">{t('세')}</span>
                                    </>
                                ) : (
                                    <span className="life_age_num">-</span>
                                )}
                            </div>

                            {bodyAge != null && chronoAge != null && (
                                <div className="life_age_compare">
                                    <div className="compare_item">
                                        <span className="label">{t('실제 나이')}</span>
                                        <span className="value">
                                            {chronoAge}
                                            {t('세')}
                                        </span>
                                    </div>

                                    <div className="compare_item">
                                        <span className="label">{t('신체 나이')}</span>
                                        <span className="value">
                                            {bodyAge}
                                            {t('세')}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="life_age_desc">
                                {lifeAge != null && bodyAge != null && chronoAge != null ? (
                                    <>
                                        {t('평균 기대수명')} 83.7{t('세를 기준으로')}
                                        <br />
                                        {t('실제 나이와 신체 나이의 차이를 반영하여')}
                                        <br />
                                        {t('미래 수명을 추정한 건강 참고 지표입니다.')}
                                    </>
                                ) : (
                                    <>
                                        {t('본 지표는 의학적 진단이 아닌')}
                                        <br />
                                        {t('측정 결과를 기반으로 한 건강 참고 정보입니다.')}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="minor_vitals">
                            <div className="vital_card">
                                <h3>{t('평균 심박수(HR)')}</h3>
                                <span>
                                    <i style={{ color: '#ED4D4D' }}>{Number.isFinite(HrTime) ? HrTime : '-'}</i> BPM
                                </span>
                            </div>

                            <div className="vital_card">
                                <h3>{t('평균 호흡수(RR)')}</h3>
                                <span>
                                    <i style={{ color: '#347deb' }}>{Number.isFinite(RrTime) ? RrTime : '-'}</i> BPM
                                </span>
                            </div>
                        </div>

                        <div className="stress_summary_under_bmi">
                            <h3 className="title">{t('스트레스 레벨')}</h3>

                            <div className="progress_level">
                                <div className="progress">
                                    <div className={`level_icon level${Stress_LV}`}></div>
                                </div>

                                <div className="level_text">
                                    <div className="level_text_area">{t('매우낮음')}</div>
                                    <div className="level_text_area">{t('낮음')}</div>
                                    <div className="level_text_area">{t('적정')}</div>
                                    <div className="level_text_area">{t('높음')}</div>
                                    <div className="level_text_area">{t('매우높음')}</div>
                                </div>
                            </div>

                            <div className="Stress_level">
                                <h2>{Stress_LV_TEXT}</h2>
                            </div>
                        </div>

                        <div className="stress_area">
                            <div className="doc-root">
                                <table>
                                    <tbody>
                                        <tr>
                                            <th colSpan={2}>{t('스트레스 지표')}</th>
                                            <th style={{ width: '25%' }}>{t('측정값')}</th>
                                        </tr>
                                        <tr>
                                            <td className={highlightIndex === 0 ? 'highlight' : ''} style={{ width: '20%' }}>
                                                {t('스트레스 반응성')}
                                                <br />
                                                (RMSSD)
                                            </td>
                                            <td>
                                                <div className="stress_bar">
                                                    <div className="bar1"></div>
                                                    <div className="bar2"></div>
                                                    <div className="bar3"></div>
                                                    <div className={`level_icon level${RMSSD_level}`} style={{ left: RMSSD_set + 4 + '%' }}></div>
                                                </div>
                                                <div className="level_text">
                                                    <div className="level_text_area">20</div>
                                                    <div className="level_text_area">80</div>
                                                </div>
                                            </td>
                                            <td>
                                                <span>
                                                    <strong>{RMSSD_set} ms</strong>
                                                    <br />
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className={highlightIndex === 1 ? 'highlight' : ''} style={{ width: '20%' }}>
                                                {t('스트레스 저항력')}
                                                <br />
                                                (SDNN)
                                            </td>
                                            <td>
                                                <div className="stress_bar">
                                                    <div className="bar1"></div>
                                                    <div className="bar2"></div>
                                                    <div className="bar3"></div>
                                                    <div className={`level_icon level${SDNN_level}`} style={{ left: sdnn_per + '%' }}></div>
                                                </div>
                                                <div className="level_text">
                                                    <div className="level_text_area">30</div>
                                                    <div className="level_text_area">100</div>
                                                </div>
                                            </td>
                                            <td>
                                                <span>
                                                    <strong>{SDNN_set} ms</strong>
                                                    <br />
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className={highlightIndex === 2 ? 'highlight' : ''} style={{ width: '25%' }}>
                                                {t('자율신경 건강도')}
                                                <br />
                                                (pNN50)
                                            </td>
                                            <td>
                                                <div className="stress_bar pnn">
                                                    <div className="bar1"></div>
                                                    <div className="bar2"></div>
                                                    <div className={`level_icon level${pNN50_level}`} style={{ left: pnn_per + 4 + '%' }}></div>
                                                </div>
                                                <div className="level_text">
                                                    <div className="level_text_area" style={{ textAlign: 'left', width: '100%',textIndent:'19%' }}>
                                                        20
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span>
                                                    <strong>{pNN50_set} %</strong>
                                                    <br />
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="HRV_graph">
                            <p>
                                <b>RMSSD</b> ({t('19.8 ~ 59.6 ms 정상')}):
                                <br />
                                {t(
                                    '스트레스 반응성을 나타내며, 자율신경계의 안정성을 반영함. RMSSD 값이 낮으면 긴장된 상태거나 자율신경계 균형이 불안정할 수 있음.',
                                )}
                            </p>
                            <p>
                                <b>SDNN</b> ({t('29.1 ~ 70.1 ms 정상')}):
                                <br />
                                {t(
                                    '신체의 스트레스 저항력을 나타내는 지표임. SDNN 값이 낮으면 신체가 스트레스에 적응하지 못하고, 누적된 스트레스가 있을 가능성이 높음.',
                                )}
                            </p>
                            <p>
                                <b>pNN50</b> ({t('20% 이상 건강')}):
                                <br />
                                {t(
                                    '두 심박 간격 차이가 50ms를 초과하는 비율을 나타내며, 자율신경계의 건강도를 평가함. pNN50이 낮으면 자율신경계의 유연성이 부족할 수 있음.',
                                )}
                            </p>
                            <p style={{ marginBottom: 0 }}>
                                <b>{t('수명나이(기대수명)')}</b> ({t('참고 지표')}):
                                <br />
                                {t(
                                    '수명나이(기대수명)는 측정된 심박수(HR), 호흡수(RR), HRV(RMSSD) 및 스트레스 지표를 종합해 동일 연령대 평균과 비교하여 산출한 건강 참고 지표입니다. 의료적 진단이나 실제 기대수명을 의미하지 않으며, 컨디션·생활습관·측정 환경에 따라 달라질 수 있습니다.',
                                )}
                            </p>
                        </div>
                    </div>

                    <p className="bottom_text">
                        {t(
                            '(알림) 이 리포트는 싱글케어 키오스크로 측정한 생체 신호와 스트레스 상태를 분석한 개인 건강 관리용 정보입니다. 싱글케어는 의료기기가 아니므로, 이 정보를 질병의 진단, 치료, 예방 등의 의료 목적으로 사용하지 마세요. 건강 상태를 참고하는 용도로만 활용하시기 바랍니다.',
                        )}
                    </p>
                </div>

                <div className="result_action_buttons">
                    {/* <button className="print_ico" onClick={() => handlePrint()}>
                        {t('인쇄하기')}
                    </button> */}

                    <button className="back_to_detail_btn" onClick={goBackToMonitoringDetail}>
                        {t('상세로 돌아가기')}
                    </button>
                </div>
            </main>
        </div>
    );
};

export default Result;
